import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import { trackerKeys } from './hooks';
import { activityKeys } from './leadActivity';
import { dealKeys } from '../deals/hooks';
import type { SupabaseClient } from '../../lib/supabase';
import type { Enums } from '../../types/database.types';

/**
 * What happened commercially, read back onto the lead.
 *
 * Batch 1a decided that Won is not a lead status — it is an outcome that
 * belongs to a deal — and promised the Leads list a column that rolls the
 * deals up. This is that column's data, and the two writes that fill it.
 *
 * ── Why a lead can be two things at once ───────────────────────────────────
 *
 * Kyu's reason for refusing to map Won onto Closed was repeat business: a
 * client who bought in 2025 and has a live deal now cannot be described by one
 * value, and that combined case is exactly the list worth filtering on.
 *
 * So this does not reduce to a single status. `open`, `won` and `lost` are
 * counted separately and the label says both when both are true.
 */

export type DealOutcome = Enums<'deal_outcome'>;

export interface LeadDealOutcome {
  lead_id: string;
  open_deals: number;
  won_deals: number;
  lost_deals: number;
  last_won_on: string | null;
  last_lost_on: string | null;
  open_stage: string | null;
}

const OUTCOME_COLUMNS =
  'lead_id, open_deals, won_deals, lost_deals, last_won_on, last_lost_on, open_stage';

function fail(message: string): never {
  throw new Error(message);
}

export const dealOutcomeKeys = {
  all: () => [...trackerKeys.all, 'lead-deal-outcomes'] as const,
};

export async function fetchLeadDealOutcomes(
  client: SupabaseClient,
): Promise<Map<string, LeadDealOutcome>> {
  const { data, error } = await client.from('lead_deal_outcomes').select(OUTCOME_COLUMNS);
  if (error) {
    fail(error.message);
  }
  const rows = Array.isArray(data) ? (data as unknown as LeadDealOutcome[]) : [];
  return new Map(rows.filter((row) => row.lead_id !== null).map((row) => [row.lead_id, row]));
}

export function useLeadDealOutcomes() {
  const { client } = useAuth();
  return useQuery({
    queryKey: dealOutcomeKeys.all(),
    queryFn: () => fetchLeadDealOutcomes(client),
  });
}

/**
 * The Deal outcome column, in words.
 *
 * Blank when there is no deal, which is honest rather than empty: most leads
 * never had one, and printing "None" 700 times would bury the rows that say
 * something.
 *
 * A won deal with no date reads "Won · date not recorded". Those are the
 * legacy customers batch 3a created deals for — nobody ever wrote down when
 * they bought, and the screen says so rather than showing the day the
 * migration happened to run.
 */
export function dealOutcomeLabel(outcome: LeadDealOutcome | undefined): string {
  if (outcome === undefined) {
    return '';
  }
  const parts: string[] = [];

  if (outcome.won_deals > 0) {
    const when = outcome.last_won_on;
    parts.push(when === null ? 'Won · date not recorded' : `Won ${when}`);
  }
  if (outcome.open_deals > 0) {
    const stage = outcome.open_stage;
    parts.push(stage === null || stage === '' ? 'Open' : `Open · ${stage}`);
  }
  // Only worth saying when nothing better happened. A past customer with one
  // lost bid is a customer, and leading with the loss would misread the row.
  if (parts.length === 0 && outcome.lost_deals > 0) {
    const when = outcome.last_lost_on;
    parts.push(when === null ? 'Lost' : `Lost ${when}`);
  }

  return parts.join(' · ');
}

/** The tone the column's badge takes — won reads as won even beside an open deal. */
export function dealOutcomeTone(
  outcome: LeadDealOutcome | undefined,
): 'success' | 'brand' | 'neutral' | null {
  if (outcome === undefined) {
    return null;
  }
  if (outcome.won_deals > 0) {
    return 'success';
  }
  if (outcome.open_deals > 0) {
    return 'brand';
  }
  if (outcome.lost_deals > 0) {
    return 'neutral';
  }
  return null;
}

/**
 * Raise a deal from a lead.
 *
 * Deliberately does not refuse a lead that already has one — the database does
 * not either. A second deal on a past customer is the most valuable row in the
 * tracker; the screen warns and lets them through.
 */
export function usePromoteLeadToDeal() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      leadId: string;
      pipeline: 'cg_deals' | 'partnerships';
      name?: string;
    }): Promise<string> => {
      const { data, error } = await client.rpc('promote_lead_to_deal', {
        p_lead: input.leadId,
        p_pipeline: input.pipeline,
        ...(input.name === undefined || input.name.trim() === '' ? {} : { p_name: input.name }),
      });
      if (error) {
        throw new Error(error.message);
      }
      return typeof data === 'string' ? data : '';
    },
    onSettled: (_data, _error, input) => {
      void queryClient.invalidateQueries({ queryKey: dealOutcomeKeys.all() });
      void queryClient.invalidateQueries({ queryKey: trackerKeys.leads() });
      void queryClient.invalidateQueries({ queryKey: activityKeys.forLead(input.leadId) });
    },
  });
}

/** Mark a deal won, lost, or open again. Reopening clears how it ended. */
export function useSetDealOutcome() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      dealId: string;
      outcome: DealOutcome;
      note?: string;
      closedOn?: string;
    }) => {
      const { error } = await client.rpc('set_deal_outcome', {
        p_deal: input.dealId,
        p_outcome: input.outcome,
        p_note: input.note ?? '',
        ...(input.closedOn === undefined ? {} : { p_closed_on: input.closedOn }),
      });
      if (error) {
        throw new Error(error.message);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: dealOutcomeKeys.all() });
      // Nez, Sep 8: "when I change the Stage of ORCA to Won or Lost the UI
      // does not update... if I change a different deal on the same page,
      // ORCA suddenly wakes up." set_deal_outcome writes deals.outcome, which
      // the Deals page reads under its own key. Only the Tracker's key was
      // invalidated here, so the row stayed stale until something else on the
      // page invalidated the Deals key for it. Both readers are told now.
      void queryClient.invalidateQueries({ queryKey: dealKeys.all });
    },
  });
}

/** The database's refusals, in words somebody can act on. */
export function dealErrorMessage(raw: string): string {
  if (raw.includes('closed_on_in_the_future')) {
    return 'That closing date is in the future. Check the day and year.';
  }
  if (raw.includes('unknown_pipeline')) {
    return 'Pick which pipeline this deal belongs to.';
  }
  if (raw.includes('lead_not_found')) {
    return 'That lead no longer exists. Refresh the page.';
  }
  if (raw.includes('deal_not_found')) {
    return 'That deal no longer exists. Refresh the page.';
  }
  if (raw.includes('not_a_sales_member')) {
    return 'You do not have access to the Outbound Tracker, so this cannot be saved.';
  }
  return 'That could not be saved. Try again.';
}
